/**
 * First we will load all of this project's JavaScript dependencies which
 * includes React and other libraries. It is a great starting point when
 * building robust, powerful web applications using React and Laravel.
 */

import {createBrowserRouter, Navigate, Outlet, RouterProvider} from 'react-router-dom';
import {createRoot} from 'react-dom/client';
import React from 'react';

require('./bootstrap');

window.moment = require('moment-timezone');
window.moment.locale(window.navigator.userLanguage || window.navigator.language);

import App from './components/app';
import ErrorBoundary from './components/error-boundary';

const router = createBrowserRouter([
  {
    path: '/',
    element: <App/>,
    errorElement: <ErrorBoundary/>,
    children: [
      {
        index: true,
        async lazy() {
          let {Welcome} = await import("./pages/welcome");
          return {Component: Welcome};
        },
      },
      {
        path: 'about-us',
        async lazy() {
          let {AboutUs} = await import("./pages/about-us");
          return {Component: AboutUs};
        },
      },
      {
        path: 'commands',
        async lazy() {
          let {Commands} = await import("./pages/commands");
          return {Component: Commands};
        },
      },
      {
        path: 'bdo',
        children: [
          {
            path: 'timers',
            async lazy() {
              let {BDOTimers} = await import("./pages/bdo/timers");
              return {Component: BDOTimers};
            },
          },
        ],
      },
      {
        path: 'pso2',
        element: <Outlet/>,
        children: [
          {
            path: 'global',
            element: <Outlet/>,
            children: [
              {
                path: 'emotes',
                async lazy() {
                  let {PSO2Emotes} = await import("./pages/pso2/global/shared/emotes");
                  return {Component: PSO2Emotes};
                },
              },
              {
                path: 'events',
                async lazy() {
                  let {PSO2Events} = await import("./pages/pso2/global/shared/events");
                  return {Component: PSO2Events};
                },
              },
              {
                path: 'fashion',
                async lazy() {
                  let {PSO2Fashion} = await import("./pages/pso2/global/shared/fashion");
                  return {Component: PSO2Fashion};
                },
              },
              {
                path: 'schedule',
                async lazy() {
                  let {PSO2Schedule} = await import("./pages/pso2/global/shared/schedule");
                  return {Component: PSO2Schedule};
                },
              },
              {
                path: 'serverstatus',
                async lazy() {
                  let {PSO2ServerStatus} = await import("./pages/pso2/global/shared/server-status");
                  return {Component: PSO2ServerStatus};
                },
              },
              {
                path: 'timers',
                async lazy() {
                  let {PSO2Timers} = await import("./pages/pso2/global/base/timers");
                  return {Component: PSO2Timers};
                },
              },
              {
                path: 'uq-predictions',
                async lazy() {
                  let {PSO2UrgentQuestPredictions} = await import("./pages/pso2/global/shared/uq-predictions");
                  return {Component: PSO2UrgentQuestPredictions};
                },
              },
              {
                path: 'voices',
                async lazy() {
                  let {Voices} = await import("./pages/pso2/global/shared/voices");
                  return {Component: Voices};
                },
              },
              {
                path: 'augment-upgrade-calculator',
                element: <Navigate to={'/pso2/global/tools/augment-upgrade-calculator'}/>,
              },
              {
                path: 'tools',
                element: <Outlet/>,
                children: [
                  {
                    path: 'augment-upgrade-calculator',
                    async lazy() {
                      let {AugmentUpgradeCalculator} = await import("./pages/pso2/global/base/tools/augment-upgrade-calculator");
                      return {Component: AugmentUpgradeCalculator};
                    },
                  },
                  {
                    path: 'typer',
                    async lazy() {
                      let {Typer} = await import("./pages/pso2/global/base/tools/typer");
                      return {Component: Typer};
                    },
                  },
                ],
              },
              {
                path: 'resources',
                element: <Outlet/>,
                children: [
                  {
                    path: 'catalysts',
                    async lazy() {
                      let {Catalysts} = await import("./pages/pso2/global/base/resources/catalysts");
                      return {Component: Catalysts};
                    },
                  },
                  {
                    path: 'class-levels',
                    async lazy() {
                      let {ClassLevels} = await import("./pages/pso2/global/base/resources/class-levels");
                      return {Component: ClassLevels};
                    },
                  },
                  {
                    path: 'divide-quests',
                    async lazy() {
                      let {DivideQuests} = await import("./pages/pso2/global/base/resources/divide-quests");
                      return {Component: DivideQuests};
                    },
                  },
                  {
                    path: 'triggers',
                    async lazy() {
                      let {Triggers} = await import("./pages/pso2/global/base/resources/triggers");
                      return {Component: Triggers};
                    },
                  },
                  {
                    path: 'uq-history',
                    async lazy() {
                      let {UQHistory} = await import("./pages/pso2/global/shared/resources/uq-history");
                      return {Component: UQHistory};
                    },
                    loader: async () => 'pso2',
                  },
                  {
                    path: 'uq-tendency',
                    async lazy() {
                      let {UQTendency} = await import("./pages/pso2/global/ngs/resources/uq-tendency");
                      return {Component: UQTendency};
                    },
                  },
                ],
              },
              {
                path: 'ngs',
                element: <Outlet/>,
                children: [
                  {
                    path: 'scratch-tickets',
                    element: <Outlet/>,
                    children: [
                      {
                        path: '',
                        async lazy() {
                          let {ScratchTickets} = await import("./pages/pso2/global/ngs/scratch-tickets");
                          return {Component: ScratchTickets};
                        },
                      },
                      {
                        path: ':slug',
                        async lazy() {
                          let {ScratchTicket} = await import("./pages/pso2/global/ngs/scratch-ticket");
                          return {Component: ScratchTicket};
                        },
                      },
                    ],
                  },
                  {
                    path: 'timers',
                    async lazy() {
                      let {PSO2NGSTimers} = await import("./pages/pso2/global/ngs/timers");
                      return {Component: PSO2NGSTimers};
                    },
                  },
                  {
                    path: 'time',
                    async lazy() {
                      let {PSO2NGSTime} = await import("./pages/pso2/global/ngs/time");
                      return {Component: PSO2NGSTime};
                    },
                  },
                  {
                    path: 'tools',
                    element: <Outlet/>,
                    children: [
                      {
                        path: 'augment-calculator',
                        async lazy() {
                          let {PSO2NGSResourceAugmentCalculator} = await import("./pages/pso2/global/ngs/tools/augment-calculator");
                          return {Component: PSO2NGSResourceAugmentCalculator};
                        },
                      },
                      {
                        path: 'typer',
                        async lazy() {
                          let {Typer} = await import("./pages/pso2/global/ngs/tools/typer");
                          return {Component: Typer};
                        },
                      },
                    ],
                  },
                  {
                    path: 'resources',
                    element: <Outlet/>,
                    children: [
                      {
                        path: 'alliance-mag',
                        async lazy() {
                          let {AllianceMag} = await import("./pages/pso2/global/ngs/resources/alliance-mag");
                          return {Component: AllianceMag};
                        },
                      },
                      {
                        path: 'augment-calculator',
                        element: <Navigate to={'/pso2/global/ngs/tools/augment-calculator'}/>,
                      },
                      {
                        path: 'class-levels',
                        async lazy() {
                          let {ClassLevels} = await import("./pages/pso2/global/ngs/resources/class-levels");
                          return {Component: ClassLevels};
                        },
                      },
                      {
                        path: 'gp-trees',
                        async lazy() {
                          let {GPTrees} = await import("./pages/pso2/global/ngs/resources/gp-trees");
                          return {Component: GPTrees};
                        },
                      },
                      {
                        path: 'enhancement-levels',
                        async lazy() {
                          let {EnhancementLevels} = await import("./pages/pso2/global/ngs/resources/enhancement-levels");
                          return {Component: EnhancementLevels};
                        },
                      },
                      {
                        path: 'nazuns-big-quiz',
                        async lazy() {
                          let {NazunsBigQuiz} = await import("./pages/pso2/global/ngs/resources/nazuns-big-quiz");
                          return {Component: NazunsBigQuiz};
                        },
                      },
                      {
                        path: 'preset-skills',
                        async lazy() {
                          let {PresetSkills} = await import("./pages/pso2/global/ngs/resources/preset-skills");
                          return {Component: PresetSkills};
                        },
                      },
                      {
                        path: 'texts',
                        async lazy() {
                          let {Texts} = await import("./pages/pso2/global/ngs/resources/texts");
                          return {Component: Texts};
                        },
                      },
                      {
                        path: 'uq-history',
                        async lazy() {
                          let {UQHistory} = await import("./pages/pso2/global/shared/resources/uq-history");
                          return {Component: UQHistory};
                        },
                        loader: async () => 'ngs',
                      },
                      {
                        path: 'uq-tendency',
                        async lazy() {
                          let {UQTendency} = await import("./pages/pso2/global/ngs/resources/uq-tendency");
                          return {Component: UQTendency};
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'jp',
            element: <Outlet/>,
            children: [
              {
                path: 'serverstatus',
                async lazy() {
                  let {PSO2ServerStatus} = await import("./pages/pso2/jp/server-status");
                  return {Component: PSO2ServerStatus};
                },
              },
              {
                path: 'ngs',
                element: <Outlet/>,
                children: [
                  {
                    path: 'timers',
                    async lazy() {
                      let {PSO2JPNGSTimers} = await import("./pages/pso2/jp/ngs/timers");
                      return {Component: PSO2JPNGSTimers};
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'ffxi',
        element: <Outlet/>,
        children: [
          {
            path: 'events',
            async lazy() {
              let {loader, Events} = await import("./pages/ffxi/events");
              return {Component: Events, loader};
            },
          },
          {
            path: 'records-of-eminence',
            async lazy() {
              let {RecordsOfEminence} = await import("./pages/ffxi/records-of-eminence");
              return {Component: RecordsOfEminence};
            },
          },
          {
            path: 'storage-slips',
            async lazy() {
              let {StorageSlips} = await import("./pages/ffxi/storage-slips");
              return {Component: StorageSlips};
            },
          },
          {
            path: 'vanabout',
            children: [
              {
                path: ':id?',
                async lazy() {
                  let {loader, Vanabout} = await import("./pages/ffxi/vanabout");
                  return {Component: Vanabout, loader};
                },
              },
            ]
          },
          {
            path: 'world-time',
            async lazy() {
              let {WorldTime} = await import("./pages/ffxi/world-time");
              return {Component: WorldTime};
            },
          },
        ],
      },
      {
        path: 'ffxiv',
        element: <Outlet/>,
        children: [
          {
            path: 'eureka',
            async lazy() {
              let {Eureka} = await import("./pages/ffxiv/eureka");
              return {Component: Eureka};
            },
          },
          {
            path: 'time',
            async lazy() {
              let {Weather} = await import("./pages/ffxiv/weather");
              return {Component: Weather};
            },
          },
          {
            path: 'timers',
            async lazy() {
              let {FFXIVTimers} = await import("./pages/ffxiv/timers");
              return {Component: FFXIVTimers};
            },
          },
          {
            path: 'serverstatus',
            async lazy() {
              let {FFXIVServerStatus} = await import("./pages/ffxiv/server-status");
              return {Component: FFXIVServerStatus};
            },
          },
          {
            path: 'endwalker',
            element: <Outlet/>,
            children: [
              {
                path: 'sightseeing-log',
                async lazy() {
                  let {SightseeingLog} = await import("./pages/ffxiv/sightseeing-log");
                  return {Component: SightseeingLog};
                },
              },
            ],
          },
        ],
      },
      {
        path: 'genshin',
        element: <Outlet/>,
        children: [
          {
            path: 'timers',
            async lazy() {
              let {GenshinTimers} = await import("./pages/genshin/timers");
              return {Component: GenshinTimers};
            },
          },
        ],
      },
      {
        path: 'priconne',
        element: <Outlet/>,
        children: [
          {
            path: 'clan-battles',
            async lazy() {
              let {PriconneClanBattles} = await import("./pages/priconne/clanBattles");
              return {Component: PriconneClanBattles};
            },
          },
          {
            path: 'gachas',
            async lazy() {
              let {PriconneGachas} = await import("./pages/priconne/gachas");
              return {Component: PriconneGachas};
            },
          },
        ],
      },
      {
        path: 'project-sekai',
        element: <Outlet/>,
        children: [
          {
            path: 'events',
            async lazy() {
              let {Events} = await import("./pages/project-sekai/events");
              return {Component: Events};
            },
          },
          {
            path: 'gachas',
            async lazy() {
              let {Gachas} = await import("./pages/project-sekai/gachas");
              return {Component: Gachas};
            },
          },
          {
            path: 'virtual-lives',
            async lazy() {
              let {VirtualLives} = await import("./pages/project-sekai/virtual-lives");
              return {Component: VirtualLives};
            },
          },
        ],
      },
      {
        path: 'admin',
        async lazy() {
          let {Admin} = await import("./pages/admin");
          return {Component: Admin};
        },
        children: [
          {
            path: 'pso2',
            element: <Outlet/>,
            children: [
              {
                path: 'scratch-ticket',
                async lazy() {
                  let {ScratchTicket} = await import("./pages/admin/pso2/scratch-ticket");
                  return {Component: ScratchTicket};
                },
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <ErrorBoundary>
          <div style={{padding: '1rem'}}>
            There's nothing here!
          </div>
        </ErrorBoundary>,
      },
    ],
  },
]);

const root = createRoot(document.getElementsByTagName('main').item(0));
root.render(
  <RouterProvider router={router}/>,
);
const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
[...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));
